import axios, {
  type AxiosInstance,
  type AxiosError,
  type AxiosResponse,
  // type InternalAxiosRequestConfig,
  type AxiosRequestConfig,
} from 'axios';
import { hasTokenRequestFailed, keycloak, stopSessionAfterTokenFailure } from '../../auth/keycloak';
import { stringify } from 'qs';
import { getApiUrl } from './getApiUrl';

// API Response Wrapper Interface
interface ApiResponseWrapper<T = unknown> {
  success: boolean;
  data: T;
  errors: string[] | null;
  trace_id: string;
}

// Extend the Axios config to include retry property
interface RetryableAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

// Status codes that should never be retried (client errors that won't be fixed by retrying)
const NON_RETRYABLE_STATUS_CODES = [400, 403, 404, 405, 406, 409, 410, 413, 414, 415, 422];

// Create the axios instance
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    paramsSerializer: (params) => {
      // Handle arrays by converting them to repeated parameters
      const processedParams: Record<string, unknown> = {};

      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // For arrays, we want to create multiple parameters with the same name
          // This will be handled by qs.stringify with arrayFormat: 'repeat'
          processedParams[key] = value;
        } else {
          processedParams[key] = value;
        }
      });

      const serialized = stringify(processedParams, { arrayFormat: 'repeat', indices: false });

      return serialized;
    },
    baseURL: getApiUrl(),
  });

  // Request interceptor to add authorization header
  instance.interceptors.request.use((config) => {
    if (keycloak.token) {
      config.headers.Authorization = `Bearer ${keycloak.token}`;
    }
    return config;
  });

  // Response interceptor to handle token refresh and unwrap API response
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      // Unwrap the API response wrapper structure
      // Check if response has the new wrapper structure
      if (
        response.data &&
        typeof response.data === 'object' &&
        'success' in response.data &&
        'data' in response.data
      ) {
        const wrapper = response.data as ApiResponseWrapper;

        // If there are errors in the wrapper, reject the promise
        if (!wrapper.success || (wrapper.errors && wrapper.errors.length > 0)) {
          const error = new Error(wrapper.errors?.join(', ') || 'API request failed') as Error & {
            response: AxiosResponse;
            trace_id?: string;
          };
          error.response = response;
          error.trace_id = wrapper.trace_id;
          return Promise.reject(error);
        }

        // Replace response data with unwrapped data
        response.data = wrapper.data;
      }

      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as RetryableAxiosRequestConfig;
      const status = error.response?.status;

      // Never retry client errors (except 401 for token refresh)
      if (status && NON_RETRYABLE_STATUS_CODES.includes(status)) {
        return Promise.reject(error);
      }

      // Only retry 401 for token refresh
      if (originalRequest && status === 401 && !originalRequest._retry) {
        if (hasTokenRequestFailed()) {
          return Promise.reject(error);
        }

        originalRequest._retry = true;
        try {
          const refreshed = await keycloak.updateToken(10);
          if (refreshed) {
            instance.defaults.headers.common.Authorization = `Bearer ${keycloak.token}`;
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${keycloak.token}`;
            }
            return instance(originalRequest);
          }
        } catch {
          await stopSessionAfterTokenFailure();
        }
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

// Export a default instance for direct use
export const apiClient = createAxiosInstance();

// Orval mutator function - this is what Orval will use
export const customInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig
): Promise<T> => {
  // Use the same instance for all requests
  const source = axios.CancelToken.source();

  const promise = apiClient({
    ...config,
    ...options,
    cancelToken: source.token,
  }).then(({ data }) => data);

  // Attach the cancel function to the promise
  (promise as Promise<T> & { cancel: () => void }).cancel = () => {
    source.cancel('Query was cancelled');
  };

  return promise;
};
